/* ============================================================
   Pac Pac Bounce — vertical bounce arcade game.
   Vanilla JS, no frameworks, no external libs.
   Mounted on [data-pacpac-bounce] by sections/pacpac-bounce.liquid.

   All tuning lives in CONFIG. Item art is pre-rendered flat-colour
   canvas shapes (see paintGood/paintHazard) so real icon assets can
   be swapped in later without touching game logic.
   ============================================================ */
(function () {
  'use strict';

  var CONFIG = {
    W: 400, // logical canvas width
    H: 700, // logical canvas height
    MAX_DPR: 2, // cap backing-store resolution
    MAX_DT: 0.05, // s — clamp so tab switches don't teleport the character

    GRAVITY: 1400, // px/s²
    LAUNCH_V0: 950, // px/s — first launch speed
    LAUNCH_GROWTH: 1.02, // per-bounce multiplier (spec said 1.04 — see handback)
    LAUNCH_MAX: 1120, // px/s — apex ~80px from canvas top (spec's 1500 overshoots)

    STEER_LERP: 0.25, // per-60fps-frame lerp toward target x
    KEY_SPEED: 420, // px/s — arrow-key steering

    CHAR_W: 84, // character display width (height follows PNG aspect)
    CHAR_HITBOX: 0.38, // hitbox radius as a fraction of sprite width
    CHAR_SQUASH_MS: 300, // contact squash duration

    BLOB_TOP: 590, // resting surface y at canvas center
    BLOB_EDGE_DROP: 30, // mound is this much lower at the edges
    BLOB_COLOR: '#b4d5a2',
    BLOB_SQUASH_MS: 350, // squash-and-stretch recovery on contact

    ITEM_SIZE_GOOD: 44, // drawn size, logical px (art authored at 34 — scaled up for legibility)
    ITEM_SIZE_HAZARD: 52, // hazards drawn larger still so the threat reads at a glance
    ITEM_RADIUS_GOOD: 0.45, // collision radius fraction — forgiving catches
    ITEM_RADIUS_HAZARD: 0.36, // tighter than goods so the bigger art doesn't spike difficulty
    ITEM_BASE_SPEED: 140, // px/s
    ITEM_SPEED_RAMP: 0.03, // +3% per bounce
    ITEM_SPEED_CAP: 2.2, // max item speed multiplier
    SPAWN_MS: 900, // starting spawn interval
    SPAWN_DECAY: 0.98, // interval multiplier per bounce
    SPAWN_MS_MIN: 450,
    HAZARD_P0: 0.25,
    HAZARD_P_MAX: 0.4,
    HAZARD_RAMP: 20, // bounces until hazard probability maxes out
    MAX_ITEMS: 8,
    BAND_MIN: 0.15, // spawn altitude band, fraction of canvas height
    BAND_MAX: 0.75,

    LIVES: 3,
    INVULN_MS: 1200,

    CATCH_POINTS: 10,
    COMBO_STEP: 0.5,
    COMBO_MAX: 5,
    BOUNCE_BONUS: 1,

    HAZARD_COLOR: '#e5484d',
    HAZARD_TINT: 0.62, // red tint over uploaded hazard art so "red = bad" reads instantly
                       // (0.45 left the green bottle looking muddy olive, not red)
    BEST_KEY: 'pacpac-bounce-best'
  };

  /* ---------- tracking (GA4 + Meta Pixel, both optional) ---------- */

  function track(name, params) {
    try {
      if (window.dataLayer && window.dataLayer.push) {
        var payload = { event: name };
        for (var k in params) payload[k] = params[k];
        window.dataLayer.push(payload);
      }
      if (typeof window.gtag === 'function') window.gtag('event', name, params);
      if (typeof window.fbq === 'function') window.fbq('trackCustom', name, params);
    } catch (e) {
      /* trackers absent or blocked — game must keep running */
    }
  }

  /* ---------- easing ---------- */

  function easeOutElastic(t) {
    if (t <= 0) return 0;
    if (t >= 1) return 1;
    var p = 0.4;
    return Math.pow(2, -10 * t) * Math.sin(((t - p / 4) * (2 * Math.PI)) / p) + 1;
  }

  function clamp(v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
  }

  /* hex → rgb, and a lighten/darken (f>1 toward white, f<1 toward black) —
     used to derive jelly gradient shades from the configured blob color */
  function hexToRgb(h) {
    h = String(h || '').trim().replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    var n = parseInt(h, 16);
    if (isNaN(n)) return { r: 180, g: 213, b: 162 };
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  }

  function shade(rgb, f) {
    function m(c) {
      var v = f > 1 ? c + (255 - c) * (f - 1) : c * f;
      return Math.max(0, Math.min(255, Math.round(v)));
    }
    return 'rgb(' + m(rgb.r) + ',' + m(rgb.g) + ',' + m(rgb.b) + ')';
  }

  /* ---------- item art (pre-rendered flat shapes, 2x cells) ----------
     Each sprite is a 96×96 canvas holding a 48×48-logical cell:
     the item occupies the central ~34px; hazards get a red glow
     baked into the margin. */

  var CELL = 48; // logical cell size (item art occupies the central ~34px)
  var ART_BOX = 34; // the size items were authored at inside the cell
  var SS = 3; // supersample factor (cells now draw upscaled, so render them sharper)

  /* rounded-rect path helper — ctx.roundRect is missing on Safari <16 */
  function rrect(g, x, y, w, h, r) {
    g.beginPath();
    g.moveTo(x + r, y);
    g.arcTo(x + w, y, x + w, y + h, r);
    g.arcTo(x + w, y + h, x, y + h, r);
    g.arcTo(x, y + h, x, y, r);
    g.arcTo(x, y, x + w, y, r);
    g.closePath();
  }

  function makeSprite(paint, glow) {
    var c = document.createElement('canvas');
    c.width = CELL * SS;
    c.height = CELL * SS;
    var g = c.getContext('2d');
    g.scale(SS, SS);
    g.translate(CELL / 2, CELL / 2);
    if (glow) {
      var grad = g.createRadialGradient(0, 0, 4, 0, 0, 23);
      grad.addColorStop(0, 'rgba(229, 72, 77, 0.38)');
      grad.addColorStop(1, 'rgba(229, 72, 77, 0)');
      g.fillStyle = grad;
      g.fillRect(-24, -24, 48, 48);
    }
    paint(g);
    return c;
  }

  var GOOD_PAINTERS = {
    yuzu: function (g) {
      g.fillStyle = '#ffd166';
      g.strokeStyle = '#e8a83a';
      g.lineWidth = 3;
      g.beginPath();
      g.arc(0, 0, 15, 0, 7);
      g.fill();
      g.stroke();
      g.fillStyle = '#fff3d6';
      g.beginPath();
      g.arc(0, 0, 10.5, 0, 7);
      g.fill();
      g.strokeStyle = '#f0b64f';
      g.lineWidth = 2;
      for (var i = 0; i < 5; i++) {
        var a = (i / 5) * Math.PI * 2;
        g.beginPath();
        g.moveTo(Math.cos(a) * 2.5, Math.sin(a) * 2.5);
        g.lineTo(Math.cos(a) * 9, Math.sin(a) * 9);
        g.stroke();
      }
    },
    berry: function (g) {
      g.fillStyle = '#6f6fd6';
      var pts = [[-6, 3], [6, 3], [0, -6]];
      for (var i = 0; i < 3; i++) {
        g.beginPath();
        g.arc(pts[i][0], pts[i][1], 8.5, 0, 7);
        g.fill();
      }
      g.fillStyle = '#9d9df0';
      g.beginPath();
      g.arc(-8, 0, 2.5, 0, 7);
      g.arc(3, -8, 2.5, 0, 7);
      g.fill();
      g.fillStyle = '#7fae5a';
      g.beginPath();
      g.ellipse(1, -14, 4.5, 2.2, -0.5, 0, 7);
      g.fill();
    },
    peach: function (g) {
      g.fillStyle = '#ffb3c1';
      g.beginPath();
      g.arc(0, 1, 14, 0, 7);
      g.fill();
      g.strokeStyle = '#e88aa0';
      g.lineWidth = 2;
      g.beginPath();
      g.arc(-5, 1, 12, -1.1, 1.1);
      g.stroke();
      g.fillStyle = '#8fbf6a';
      g.beginPath();
      g.ellipse(4, -13, 5.5, 2.6, 0.6, 0, 7);
      g.fill();
    },
    blossom: function (g) {
      g.fillStyle = '#f7a8c4';
      for (var i = 0; i < 5; i++) {
        var a = (i / 5) * Math.PI * 2 - Math.PI / 2;
        g.beginPath();
        g.arc(Math.cos(a) * 8.5, Math.sin(a) * 8.5, 7, 0, 7);
        g.fill();
      }
      g.fillStyle = '#ffd166';
      g.beginPath();
      g.arc(0, 0, 5, 0, 7);
      g.fill();
    },
    mango: function (g) {
      g.fillStyle = '#ffa94d';
      g.beginPath();
      g.ellipse(0, 1, 15, 11, 0.5, 0, 7);
      g.fill();
      g.fillStyle = 'rgba(255,255,255,0.45)';
      g.beginPath();
      g.ellipse(-5, -4, 5, 2.6, 0.5, 0, 7);
      g.fill();
      g.fillStyle = '#7fae5a';
      g.beginPath();
      g.ellipse(10, -11, 5, 2.4, 0.9, 0, 7);
      g.fill();
    },
    matcha: function (g) {
      g.fillStyle = '#7fae5a';
      g.beginPath();
      g.moveTo(0, -15);
      g.bezierCurveTo(12, -8, 12, 8, 0, 15);
      g.bezierCurveTo(-12, 8, -12, -8, 0, -15);
      g.fill();
      g.strokeStyle = '#5d8742';
      g.lineWidth = 2;
      g.beginPath();
      g.moveTo(0, -12);
      g.lineTo(0, 12);
      g.stroke();
    }
  };

  /* real item art (assets/game-item-<key>.png) fitted into the same cell;
     hazards get the code-drawn red glow PLUS a red tint composited over the
     art — the uploaded hazard art is naturalistic (white sugar, golden wheat,
     green bottle) and would otherwise read as safe */
  function makeSpriteFromImage(img, glow) {
    return makeSprite(function (g) {
      var s = Math.min(ART_BOX / img.naturalWidth, ART_BOX / img.naturalHeight);
      var w = img.naturalWidth * s;
      var h = img.naturalHeight * s;
      g.drawImage(img, -w / 2, -h / 2, w, h);
      if (glow) {
        g.globalCompositeOperation = 'source-atop';
        g.globalAlpha = CONFIG.HAZARD_TINT;
        g.fillStyle = CONFIG.HAZARD_COLOR;
        g.fillRect(-CELL / 2, -CELL / 2, CELL, CELL);
        g.globalCompositeOperation = 'source-over';
        g.globalAlpha = 1;
      }
    }, glow);
  }

  var HAZARD_PAINTERS = {
    sugar: function (g) {
      g.fillStyle = '#f06a72';
      g.strokeStyle = '#c73a41';
      g.lineWidth = 2.5;
      rrect(g, -13, -11, 26, 24, 5);
      g.fill();
      g.stroke();
      g.fillStyle = '#f8949a';
      rrect(g, -13, -11, 26, 9, 5);
      g.fill();
      g.fillStyle = 'rgba(255,255,255,0.75)';
      g.beginPath();
      g.arc(-5, 3, 1.6, 0, 7);
      g.arc(4, 7, 1.6, 0, 7);
      g.arc(1, -6, 1.6, 0, 7);
      g.fill();
    },
    wheat: function (g) {
      g.strokeStyle = '#c73a41';
      g.lineWidth = 2.5;
      g.beginPath();
      g.moveTo(0, 16);
      g.lineTo(0, -6);
      g.stroke();
      g.fillStyle = '#e5484d';
      for (var i = 0; i < 3; i++) {
        var y = -4 - i * 5;
        g.beginPath();
        g.ellipse(-5, y, 4.5, 2.6, -0.9, 0, 7);
        g.ellipse(5, y, 4.5, 2.6, 0.9, 0, 7);
        g.fill();
      }
      g.beginPath();
      g.ellipse(0, -17, 2.8, 5, 0, 0, 7);
      g.fill();
    },
    bottle: function (g) {
      g.fillStyle = '#c73a41';
      g.fillRect(-4, -17, 8, 6);
      g.fillStyle = '#e5484d';
      rrect(g, -9, -11, 18, 28, 6);
      g.fill();
      g.fillStyle = '#f8949a';
      g.fillRect(-9, -2, 18, 9);
      g.fillStyle = '#c73a41';
      g.beginPath();
      g.arc(0, 2.5, 3, 0, 7);
      g.fill();
    }
  };

  /* ---------- per-stage game ---------- */

  function initGame(root) {
    var canvas = root.querySelector('.pb-canvas');
    if (!canvas || !canvas.getContext) return;
    var ctx = canvas.getContext('2d');

    var hud = root.querySelector('.pb-hud');
    var scoreEl = root.querySelector('.pb-hud__score');
    var sachets = root.querySelectorAll('.pb-sachet');
    var selectOverlay = root.querySelector('.pb-overlay--select');
    var overOverlay = root.querySelector('.pb-overlay--over');
    var startBtn = root.querySelector('[data-pb-start]');
    var replayBtn = root.querySelector('[data-pb-replay]');
    var changeBtn = root.querySelector('[data-pb-change]');
    var shareBtn = root.querySelector('[data-pb-share]');
    var charBtns = root.querySelectorAll('[data-pb-char]');
    var finalScoreEl = root.querySelector('[data-pb-final-score]');
    var bestScoreEl = root.querySelector('[data-pb-best-score]');
    var mcForm = root.querySelector('[data-pb-mc]');
    var menuBtn = root.querySelector('[data-pb-menu]');
    var pauseOverlay = root.querySelector('.pb-overlay--pause');
    var resumeBtn = root.querySelector('[data-pb-resume]');
    var exitBtns = root.querySelectorAll('[data-pb-exit]');

    var seigaihaSrc = root.getAttribute('data-seigaiha') || '';
    var shareCopy = root.getAttribute('data-share-copy') || '';
    var exitUrl = root.getAttribute('data-exit-url') || '/';

    /* jelly blob color (+ derived gloss shades) — the bounce platform is drawn
       to look like translucent konjac jelly. Kept OFF the red palette on
       purpose: red is the game's hazard signal. */
    var blobColor = root.getAttribute('data-blob-color') || CONFIG.BLOB_COLOR;
    var blobRgb = hexToRgb(blobColor);
    var blobLight = shade(blobRgb, 1.32);
    var blobDark = shade(blobRgb, 0.82);
    /* suspended air bubbles — the signature look of real konjac jelly.
       Positions fixed at init so they don't flicker frame to frame. */
    var bubbles = [];
    for (var bb = 0; bb < 15; bb++) {
      bubbles.push({
        x: 26 + Math.random() * (CONFIG.W - 52),
        y: CONFIG.BLOB_TOP + 16 + Math.random() * 82,
        r: 1.6 + Math.random() * 4.2,
        ph: Math.random() * 6.28 // bob phase
      });
    }

    /* --- canvas backing store: fixed logical size × capped DPR --- */
    var dpr = Math.min(window.devicePixelRatio || 1, CONFIG.MAX_DPR);
    canvas.width = CONFIG.W * dpr;
    canvas.height = CONFIG.H * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    var headingFont =
      (getComputedStyle(document.documentElement).getPropertyValue('--heading-font-stack') ||
        'sans-serif').trim() || 'sans-serif';

    /* --- pre-rendered item sprites ---
       Painted flat shapes are the v1 fallback. When real art exists as
       assets/game-item-<key>.png, the section passes each URL via a
       data-art-<key> attribute and the sprite is swapped in on load. */
    var GOOD_KEYS = ['yuzu', 'berry', 'peach', 'blossom', 'mango', 'matcha'];
    var HAZARD_KEYS = ['sugar', 'wheat', 'bottle'];
    var goodSprites = GOOD_KEYS.map(function (k) {
      return makeSprite(GOOD_PAINTERS[k], false);
    });
    var hazardSprites = HAZARD_KEYS.map(function (k) {
      return makeSprite(HAZARD_PAINTERS[k], true);
    });

    function loadItemArt(keys, sprites, glow) {
      keys.forEach(function (k, i) {
        var src = root.getAttribute('data-art-' + k);
        if (!src) return;
        var img = new Image();
        img.onload = function () {
          sprites[i] = makeSpriteFromImage(img, glow);
        };
        /* missing/404 art → onload never fires → painted fallback stays */
        img.src = src;
      });
    }
    loadItemArt(GOOD_KEYS, goodSprites, false);
    loadItemArt(HAZARD_KEYS, hazardSprites, true);

    /* --- static background layer (washi tint + seigaiha band) --- */
    var bg = document.createElement('canvas');
    bg.width = CONFIG.W * dpr;
    bg.height = CONFIG.H * dpr;
    renderBackground(null);
    if (seigaihaSrc) {
      var tile = new Image();
      tile.onload = function () {
        renderBackground(tile);
      };
      tile.src = seigaihaSrc;
    }

    function renderBackground(tileImg) {
      var g = bg.getContext('2d');
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
      g.fillStyle = '#fbf5e6';
      g.fillRect(0, 0, CONFIG.W, CONFIG.H);
      /* washi speckle: sparse warm flecks, painted once */
      g.fillStyle = 'rgba(178, 160, 120, 0.07)';
      for (var i = 0; i < 90; i++) {
        var x = Math.random() * CONFIG.W;
        var y = Math.random() * CONFIG.H;
        g.beginPath();
        g.ellipse(x, y, 1 + Math.random() * 2.2, 0.6 + Math.random(), Math.random() * 3, 0, 7);
        g.fill();
      }
      /* faint seigaiha band behind the blob */
      if (tileImg) {
        var pat = g.createPattern(tileImg, 'repeat');
        if (pat) {
          g.save();
          g.globalAlpha = 0.13;
          g.translate(0, 470);
          g.scale(0.28, 0.28);
          g.fillStyle = pat;
          g.fillRect(0, 0, CONFIG.W / 0.28, 230 / 0.28);
          g.restore();
          /* fade the band's top edge into the paper */
          var fade = g.createLinearGradient(0, 470, 0, 545);
          fade.addColorStop(0, 'rgba(251, 245, 230, 1)');
          fade.addColorStop(1, 'rgba(251, 245, 230, 0)');
          g.fillStyle = fade;
          g.fillRect(0, 470, CONFIG.W, 75);
        }
      }
    }

    /* --- character images --- */
    var charImgs = {}; // key -> {img, ready}
    charBtns.forEach(function (btn) {
      var k = btn.getAttribute('data-pb-char');
      var img = new Image();
      var entry = { img: img, ready: false };
      img.onload = function () {
        entry.ready = true;
      };
      img.src = btn.getAttribute('data-pb-src');
      charImgs[k] = entry;
    });

    /* --- object pools (no per-frame allocation) --- */
    var items = [];
    for (var ii = 0; ii < CONFIG.MAX_ITEMS; ii++) {
      items.push({ active: false, x: 0, y: 0, vx: 0, sprite: null, good: true, seed: 0, r: 0, d: CELL });
    }
    var floats = [];
    for (var fi = 0; fi < 10; fi++) {
      floats.push({ active: false, x: 0, y: 0, age: 0, text: '', bad: false });
    }

    /* --- state --- */
    var st = {
      mode: 'select', // 'select' | 'playing' | 'paused' | 'over'
      resumeMode: 'select', // mode to restore when the pause menu closes
      charKey: 'jella',
      x: CONFIG.W / 2,
      y: 300,
      vy: 0,
      targetX: CONFIG.W / 2,
      launchV: CONFIG.LAUNCH_V0,
      bounces: 0,
      score: 0,
      lives: CONFIG.LIVES,
      combo: 1,
      invuln: 0,
      spawnTimer: CONFIG.SPAWN_MS,
      blobSquashT: 1e9,
      charSquashT: 1e9,
      hitShakeT: 1e9, // time since last hazard hit (drives the ouch shudder)
      flashT: 1e9, // time since last hazard hit (drives the red screen flash)
      keyL: false,
      keyR: false,
      time: 0,
      shownScore: -1,
      best: 0
    };
    try {
      st.best = parseInt(localStorage.getItem(CONFIG.BEST_KEY), 10) || 0;
    } catch (e) { /* storage unavailable (private mode) — best stays session-only */ }

    function blobSurfaceY(x) {
      var n = (x / CONFIG.W) * 2 - 1;
      return CONFIG.BLOB_TOP + CONFIG.BLOB_EDGE_DROP * n * n + Math.sin(st.time * 1.6 + x * 0.02) * 4;
    }

    /* --- spawning --- */
    function itemSpeed() {
      return CONFIG.ITEM_BASE_SPEED *
        Math.min(1 + st.bounces * CONFIG.ITEM_SPEED_RAMP, CONFIG.ITEM_SPEED_CAP);
    }

    function spawnInterval() {
      return Math.max(CONFIG.SPAWN_MS * Math.pow(CONFIG.SPAWN_DECAY, st.bounces), CONFIG.SPAWN_MS_MIN);
    }

    function spawnItem() {
      var slot = null;
      for (var i = 0; i < items.length; i++) {
        if (!items[i].active) { slot = items[i]; break; }
      }
      if (!slot) return; // 8 concurrent max
      var hazardP = CONFIG.HAZARD_P0 +
        (CONFIG.HAZARD_P_MAX - CONFIG.HAZARD_P0) * Math.min(st.bounces / CONFIG.HAZARD_RAMP, 1);
      var isHazard = Math.random() < hazardP;
      var list = isHazard ? hazardSprites : goodSprites;
      var dir = Math.random() < 0.5 ? 1 : -1;
      var size = isHazard ? CONFIG.ITEM_SIZE_HAZARD : CONFIG.ITEM_SIZE_GOOD;
      slot.active = true;
      slot.good = !isHazard;
      slot.sprite = list[(Math.random() * list.length) | 0];
      slot.r = size * (isHazard ? CONFIG.ITEM_RADIUS_HAZARD : CONFIG.ITEM_RADIUS_GOOD);
      slot.d = CELL * (size / ART_BOX); // scale the whole cell so glow margins scale too
      slot.vx = dir * itemSpeed();
      slot.x = dir > 0 ? -CELL : CONFIG.W + CELL;
      slot.y = CONFIG.H * (CONFIG.BAND_MIN + Math.random() * (CONFIG.BAND_MAX - CONFIG.BAND_MIN));
      slot.seed = Math.random() * 6.28;
    }

    function pushFloat(x, y, text, bad) {
      for (var i = 0; i < floats.length; i++) {
        if (!floats[i].active) {
          floats[i].active = true;
          floats[i].x = x;
          floats[i].y = y;
          floats[i].age = 0;
          floats[i].text = text;
          floats[i].bad = !!bad;
          return;
        }
      }
    }

    /* --- lifecycle --- */
    function resetRun() {
      for (var i = 0; i < items.length; i++) items[i].active = false;
      for (var j = 0; j < floats.length; j++) floats[j].active = false;
      st.x = CONFIG.W / 2;
      st.y = 260;
      st.vy = 0;
      st.targetX = CONFIG.W / 2;
      st.launchV = CONFIG.LAUNCH_V0;
      st.bounces = 0;
      st.score = 0;
      st.lives = CONFIG.LIVES;
      st.combo = 1;
      st.invuln = 0;
      st.spawnTimer = CONFIG.SPAWN_MS;
      st.blobSquashT = 1e9;
      st.charSquashT = 1e9;
      st.hitShakeT = 1e9;
      st.flashT = 1e9;
      st.shownScore = -1;
      sachets.forEach(function (s) {
        s.classList.remove('is-out');
      });
    }

    function startRun() {
      resetRun();
      st.mode = 'playing';
      selectOverlay.hidden = true;
      overOverlay.hidden = true;
      if (pauseOverlay) pauseOverlay.hidden = true;
      hud.hidden = false;
      updateMenuBtn();
      canvas.focus({ preventScroll: true });
    }

    function endRun() {
      st.mode = 'over';
      if (st.score > st.best) {
        st.best = st.score;
        try {
          localStorage.setItem(CONFIG.BEST_KEY, String(st.best));
        } catch (e) { /* ignore */ }
      }
      finalScoreEl.textContent = String(st.score);
      bestScoreEl.textContent = String(st.best);
      overOverlay.hidden = false;
      hud.hidden = true;
      updateMenuBtn();
      track('game_over', { score: st.score, bounces: st.bounces });
    }

    function onHazardHit(item) {
      item.active = false;
      st.lives--;
      st.combo = 1;
      st.invuln = CONFIG.INVULN_MS / 1000;
      st.hitShakeT = 0; // trigger the character shudder
      st.flashT = 0; // trigger the red screen-edge flash
      pushFloat(st.x, st.y - 6, 'Ouch!', true);
      var sachet = sachets[CONFIG.LIVES - 1 - st.lives];
      if (sachet) sachet.classList.add('is-out');
      if (st.lives <= 0) endRun();
    }

    /* --- pause / exit menu ---
       Available from the persistent top-right corner button. Pausing just
       parks the mode; the loop keeps rendering the frozen frame, and physics
       only advances in 'playing', so no extra freeze logic is needed. */
    function updateMenuBtn() {
      if (menuBtn) menuBtn.hidden = st.mode === 'paused';
    }

    function openMenu() {
      if (!pauseOverlay || st.mode === 'paused') return;
      st.resumeMode = st.mode;
      st.mode = 'paused';
      pauseOverlay.hidden = false;
      updateMenuBtn();
      if (st.resumeMode === 'playing') track('game_pause', { score: st.score });
    }

    function closeMenu() {
      if (st.mode !== 'paused') return;
      st.mode = st.resumeMode;
      if (pauseOverlay) pauseOverlay.hidden = true;
      updateMenuBtn();
      if (st.mode === 'playing') canvas.focus({ preventScroll: true });
    }

    function exitGame() {
      track('game_exit', { score: st.score });
      window.location.href = exitUrl;
    }

    function onCatch(item) {
      item.active = false;
      var pts = Math.round(CONFIG.CATCH_POINTS * st.combo);
      st.score += pts;
      var label = '+' + pts + (st.combo > 1 ? ' ×' + (st.combo % 1 ? st.combo.toFixed(1) : st.combo) : '');
      pushFloat(item.x, item.y - 10, label);
      st.combo = Math.min(st.combo + CONFIG.COMBO_STEP, CONFIG.COMBO_MAX);
    }

    /* --- input --- */
    function pointerToX(e) {
      var rect = canvas.getBoundingClientRect();
      return ((e.clientX - rect.left) / rect.width) * CONFIG.W;
    }

    canvas.addEventListener('pointerdown', function (e) {
      if (st.mode !== 'playing') return;
      canvas.setPointerCapture(e.pointerId);
      st.targetX = pointerToX(e);
      e.preventDefault();
    });
    canvas.addEventListener('pointermove', function (e) {
      if (st.mode !== 'playing') return;
      st.targetX = pointerToX(e);
    });

    window.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.key === 'p' || e.key === 'P') {
        if (st.mode === 'playing') openMenu();
        else if (st.mode === 'paused') closeMenu();
        return;
      }
      if (st.mode !== 'playing') return;
      if (e.key === 'ArrowLeft') { st.keyL = true; e.preventDefault(); }
      else if (e.key === 'ArrowRight') { st.keyR = true; e.preventDefault(); }
    });
    window.addEventListener('keyup', function (e) {
      if (e.key === 'ArrowLeft') st.keyL = false;
      else if (e.key === 'ArrowRight') st.keyR = false;
    });

    /* --- character select / buttons --- */
    charBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        charBtns.forEach(function (b) {
          b.classList.toggle('is-selected', b === btn);
        });
        st.charKey = btn.getAttribute('data-pb-char');
      });
    });

    startBtn.addEventListener('click', function () {
      startRun();
      track('game_start', { character: st.charKey });
    });

    replayBtn.addEventListener('click', function () {
      startRun();
      track('game_replay', { character: st.charKey });
    });

    if (changeBtn) {
      changeBtn.addEventListener('click', function () {
        st.mode = 'select';
        overOverlay.hidden = true;
        selectOverlay.hidden = false;
        updateMenuBtn();
      });
    }

    if (menuBtn) menuBtn.addEventListener('click', openMenu);
    if (resumeBtn) resumeBtn.addEventListener('click', closeMenu);
    for (var ei = 0; ei < exitBtns.length; ei++) {
      exitBtns[ei].addEventListener('click', exitGame);
    }

    /* --- share: Instagram-story score card (1080×1920) ---
       Generates a branded portrait card and hands it to the native share
       sheet (files → user picks Instagram → Stories). Fallback chain:
       text share → card download + copy text → copy text only. */
    var STORY_W = 1080;
    var STORY_H = 1920;
    var logoSrc = root.getAttribute('data-logo') || '';
    var stageStyles = getComputedStyle(root);
    var accent = (stageStyles.getPropertyValue('--pb-accent') || '').trim() || '#00b390';
    var btnTextColor = (stageStyles.getPropertyValue('--pb-btn-text') || '').trim() || '#fcf6b1';

    /* CORS-clean image copies for the card (the card canvas must stay
       untainted for toBlob; a failed CORS load degrades to null, never
       breaks the in-game sprites, which load separately without CORS) */
    var corsCache = {};
    function loadCors(src, cb) {
      if (!src) { cb(null); return; }
      var entry = corsCache[src];
      if (entry) {
        if (entry.done) cb(entry.img);
        else entry.cbs.push(cb);
        return;
      }
      entry = corsCache[src] = { img: null, done: false, cbs: [cb] };
      var img = new Image();
      img.crossOrigin = 'anonymous';
      function finish(ok) {
        entry.img = ok ? img : null;
        entry.done = true;
        for (var i = 0; i < entry.cbs.length; i++) entry.cbs[i](entry.img);
      }
      img.onload = function () { finish(true); };
      img.onerror = function () { finish(false); };
      img.src = src;
    }

    function selectedCharSrc() {
      for (var i = 0; i < charBtns.length; i++) {
        if (charBtns[i].getAttribute('data-pb-char') === st.charKey) {
          return charBtns[i].getAttribute('data-pb-src');
        }
      }
      return null;
    }

    function drawStoryCard(charImg, tileImg, logoImg) {
      var c = document.createElement('canvas');
      c.width = STORY_W;
      c.height = STORY_H;
      var g = c.getContext('2d');

      /* washi paper */
      g.fillStyle = '#fbf5e6';
      g.fillRect(0, 0, STORY_W, STORY_H);
      g.fillStyle = 'rgba(178, 160, 120, 0.07)';
      for (var i = 0; i < 260; i++) {
        g.beginPath();
        g.ellipse(Math.random() * STORY_W, Math.random() * STORY_H,
          2 + Math.random() * 5, 1.5 + Math.random() * 2.5, Math.random() * 3, 0, 7);
        g.fill();
      }

      /* seigaiha band behind the mound */
      if (tileImg) {
        var pat = g.createPattern(tileImg, 'repeat');
        if (pat) {
          g.save();
          g.globalAlpha = 0.13;
          g.translate(0, 1400);
          g.scale(0.75, 0.75);
          g.fillStyle = pat;
          g.fillRect(0, 0, STORY_W / 0.75, 520 / 0.75);
          g.restore();
          var fade = g.createLinearGradient(0, 1400, 0, 1600);
          fade.addColorStop(0, 'rgba(251, 245, 230, 1)');
          fade.addColorStop(1, 'rgba(251, 245, 230, 0)');
          g.fillStyle = fade;
          g.fillRect(0, 1400, STORY_W, 200);
        }
      }

      /* jelly mound */
      var grad = g.createLinearGradient(0, 1580, 0, STORY_H);
      grad.addColorStop(0, blobLight);
      grad.addColorStop(1, blobColor);
      g.fillStyle = grad;
      g.beginPath();
      g.moveTo(-40, STORY_H + 40);
      for (var x = -40; x <= STORY_W + 40; x += 54) {
        var n = (x / STORY_W) * 2 - 1;
        g.lineTo(x, 1640 + 90 * n * n + Math.sin(x * 0.012) * 10);
      }
      g.lineTo(STORY_W + 40, STORY_H + 40);
      g.closePath();
      g.fill();
      g.fillStyle = 'rgba(255,255,255,0.35)';
      g.beginPath();
      g.ellipse(STORY_W * 0.3, 1710, 120, 24, -0.12, 0, 7);
      g.fill();

      /* logo + title */
      g.textAlign = 'center';
      var titleY = 250;
      if (logoImg) {
        var lw = 460;
        var lh = lw * (logoImg.naturalHeight / logoImg.naturalWidth);
        g.drawImage(logoImg, (STORY_W - lw) / 2, 110, lw, lh);
        titleY = 110 + lh + 130;
      }
      g.fillStyle = '#3e7a53';
      g.font = '700 96px ' + headingFont;
      g.fillText('PAC PAC BOUNCE', STORY_W / 2, titleY);

      /* score */
      g.fillStyle = '#6a6a55';
      g.font = '700 64px ' + headingFont;
      g.fillText('I scored', STORY_W / 2, 760);
      g.fillStyle = '#3e7a53';
      g.font = '700 320px ' + headingFont;
      g.fillText(String(st.score), STORY_W / 2, 1070);

      /* character on the mound (slight tilt for energy) */
      if (charImg) {
        var cw = 440;
        var ch = cw * (charImg.naturalHeight / charImg.naturalWidth);
        g.fillStyle = 'rgba(90, 110, 80, 0.2)';
        g.beginPath();
        g.ellipse(STORY_W / 2, 1660, 190, 42, 0, 0, 7);
        g.fill();
        g.save();
        g.translate(STORY_W / 2, 1640 - ch / 2);
        g.rotate(-0.1);
        g.drawImage(charImg, -cw / 2, -ch / 2, cw, ch);
        g.restore();
      }

      /* site pill */
      g.fillStyle = accent;
      rrect(g, STORY_W / 2 - 280, 1790, 560, 88, 44);
      g.fill();
      g.fillStyle = btnTextColor;
      g.font = '700 42px ' + headingFont;
      g.fillText(location.host.replace('www.', '') + '/pages/play', STORY_W / 2, 1848);

      return c;
    }

    function buildStoryCard(done) {
      loadCors(selectedCharSrc(), function (charImg) {
        loadCors(seigaihaSrc, function (tileImg) {
          loadCors(logoSrc, function (logoImg) {
            try {
              done(drawStoryCard(charImg, tileImg, logoImg));
            } catch (e) {
              done(null);
            }
          });
        });
      });
    }

    function copiedFeedback() {
      shareBtn.classList.add('is-copied');
      setTimeout(function () {
        shareBtn.classList.remove('is-copied');
      }, 1600);
    }

    if (shareBtn) {
      shareBtn.addEventListener('click', function () {
        var text = shareCopy.replace('{score}', String(st.score));
        var url = location.href.split('?')[0];

        function fallbackText() {
          if (navigator.share) {
            navigator.share({ text: text, url: url }).catch(function () {});
            track('game_share', { method: 'text', score: st.score });
          } else if (navigator.clipboard) {
            navigator.clipboard.writeText(text + ' ' + url).then(copiedFeedback).catch(function () {});
            track('game_share', { method: 'copy', score: st.score });
          }
        }

        buildStoryCard(function (card) {
          if (!card) { fallbackText(); return; }
          var blobbed = false;
          try {
            card.toBlob(function (b) {
              blobbed = true;
              if (b && navigator.canShare && typeof File === 'function') {
                var file = new File([b], 'pac-pac-bounce-score.png', { type: 'image/png' });
                if (navigator.canShare({ files: [file] })) {
                  navigator.share({ files: [file], text: text }).catch(function () {});
                  track('game_share', { method: 'story', score: st.score });
                  return;
                }
              }
              if (b) {
                /* desktop: download the card + copy the text */
                var a = document.createElement('a');
                a.href = URL.createObjectURL(b);
                a.download = 'pac-pac-bounce-score.png';
                document.body.appendChild(a);
                a.click();
                a.parentNode.removeChild(a);
                setTimeout(function () { URL.revokeObjectURL(a.href); }, 4000);
                if (navigator.clipboard) {
                  navigator.clipboard.writeText(text + ' ' + url).catch(function () {});
                }
                copiedFeedback();
                track('game_share', { method: 'download', score: st.score });
                return;
              }
              fallbackText();
            }, 'image/png');
          } catch (e) {
            /* tainted canvas (CORS blocked) — SecurityError */
            if (!blobbed) fallbackText();
          }
        });
      });
    }

    /* --- Mailchimp capture (JSONP — no page nav, no libs) --- */
    if (mcForm) {
      var mcMsg = mcForm.querySelector('.pb-mc__msg');
      var mcBusy = false;
      mcForm.addEventListener('submit', function (e) {
        e.preventDefault();
        if (mcBusy) return;
        var email = mcForm.querySelector('input[type="email"]').value.trim();
        if (!email) return;
        mcBusy = true;
        mcMsg.textContent = '…';
        submitMailchimp(mcForm.getAttribute('action'), email, function (ok, msg) {
          mcBusy = false;
          mcMsg.textContent = msg;
          mcForm.classList.toggle('is-success', ok);
          if (ok) track('game_email_submit', { score: st.score });
        });
      });
    }

    function submitMailchimp(action, email, done) {
      var parts = (action || '').split('?');
      if (parts.length < 2 || action.indexOf('/subscribe/post') === -1) {
        done(false, 'Signup is not configured yet.');
        return;
      }
      var cbName = '_pbMc' + (Date.now() % 1e7);
      var script = document.createElement('script');
      var timer = setTimeout(function () {
        cleanup();
        done(false, 'No response — please try again.');
      }, 8000);
      function cleanup() {
        clearTimeout(timer);
        delete window[cbName];
        if (script.parentNode) script.parentNode.removeChild(script);
      }
      window[cbName] = function (res) {
        cleanup();
        var ok = res && res.result === 'success';
        /* Mailchimp msg can contain HTML — strip to text */
        var div = document.createElement('div');
        div.innerHTML = (res && res.msg) || '';
        done(ok, div.textContent || (ok ? 'Thanks — check your inbox!' : 'Something went wrong.'));
      };
      script.onerror = function () {
        cleanup();
        done(false, 'Something went wrong — please try again.');
      };
      script.src =
        parts[0].replace('/subscribe/post', '/subscribe/post-json') +
        '?' + parts[1] +
        '&EMAIL=' + encodeURIComponent(email) +
        '&c=' + cbName;
      document.head.appendChild(script);
    }

    /* --- update --- */
    function update(dt) {
      st.time += dt;
      if (st.blobSquashT < 10) st.blobSquashT += dt;
      if (st.charSquashT < 10) st.charSquashT += dt;
      if (st.hitShakeT < 10) st.hitShakeT += dt;
      if (st.flashT < 10) st.flashT += dt;
      if (st.mode !== 'playing') return;

      /* steering */
      if (st.keyL) st.targetX -= CONFIG.KEY_SPEED * dt;
      if (st.keyR) st.targetX += CONFIG.KEY_SPEED * dt;
      var half = CONFIG.CHAR_W / 2;
      st.targetX = clamp(st.targetX, half, CONFIG.W - half);
      /* frame-rate-independent lerp equivalent to STEER_LERP at 60fps */
      var k = 1 - Math.pow(1 - CONFIG.STEER_LERP, dt * 60);
      st.x += (st.targetX - st.x) * k;

      /* vertical physics */
      st.vy += CONFIG.GRAVITY * dt;
      st.y += st.vy * dt;

      var surface = blobSurfaceY(st.x);
      var charBottom = st.y + half; /* sprites are square: half width = half height */
      if (st.vy > 0 && charBottom >= surface) {
        st.y = surface - half;
        st.vy = -st.launchV;
        st.launchV = Math.min(st.launchV * CONFIG.LAUNCH_GROWTH, CONFIG.LAUNCH_MAX);
        st.bounces++;
        st.score += CONFIG.BOUNCE_BONUS;
        st.blobSquashT = 0;
        st.charSquashT = 0;
      }

      if (st.invuln > 0) st.invuln -= dt;

      /* items */
      var charR = CONFIG.CHAR_W * CONFIG.CHAR_HITBOX;
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        if (!it.active) continue;
        it.x += it.vx * dt;
        if (it.x < -it.d * 1.5 || it.x > CONFIG.W + it.d * 1.5) {
          it.active = false;
          continue;
        }
        var dx = it.x - st.x;
        var dy = it.y - st.y;
        var reach = charR + it.r;
        if (dx * dx + dy * dy <= reach * reach) {
          if (it.good) onCatch(it);
          else if (st.invuln <= 0) onHazardHit(it);
        }
      }

      /* spawn cadence */
      st.spawnTimer -= dt * 1000;
      if (st.spawnTimer <= 0) {
        spawnItem();
        st.spawnTimer = spawnInterval();
      }

      /* floating score labels */
      for (var f = 0; f < floats.length; f++) {
        if (!floats[f].active) continue;
        floats[f].age += dt;
        if (floats[f].age > 0.9) floats[f].active = false;
      }

      /* HUD score (DOM write only when changed) */
      if (st.score !== st.shownScore) {
        st.shownScore = st.score;
        scoreEl.textContent = String(st.score);
      }
    }

    /* --- render --- */
    var live = false; // true whenever a run is on screen (playing or paused)
    function render() {
      live = st.mode === 'playing' || st.mode === 'paused';
      ctx.drawImage(bg, 0, 0, CONFIG.W, CONFIG.H);

      /* items */
      if (live) {
        for (var i = 0; i < items.length; i++) {
          var it = items[i];
          if (!it.active) continue;
          var bob = Math.sin(st.time * 3 + it.seed) * 3;
          ctx.drawImage(it.sprite, it.x - it.d / 2, it.y + bob - it.d / 2, it.d, it.d);
        }
      }

      drawBlob();

      if (live) {
        drawShadow();
        drawCharacter();
        drawFloats();
        drawHitFlash();
      }
    }

    function drawBlob() {
      var e = easeOutElastic(Math.min(st.blobSquashT * 1000 / CONFIG.BLOB_SQUASH_MS, 1));
      var sx = 1.25 + (1 - 1.25) * e;
      var sy = 0.7 + (1 - 0.7) * e;
      ctx.save();
      ctx.translate(CONFIG.W / 2, CONFIG.H);
      ctx.scale(sx, sy);
      ctx.translate(-CONFIG.W / 2, -CONFIG.H);

      /* mound path (reused for fill + interior clip) */
      ctx.beginPath();
      ctx.moveTo(-30, CONFIG.H + 30);
      for (var x = -30; x <= CONFIG.W + 30; x += 18) {
        ctx.lineTo(x, blobSurfaceY(clamp(x, 0, CONFIG.W)));
      }
      ctx.lineTo(CONFIG.W + 30, CONFIG.H + 30);
      ctx.closePath();

      /* translucent jelly body — pale at the wobbling top, deeper at the base */
      var grad = ctx.createLinearGradient(0, CONFIG.BLOB_TOP - 30, 0, CONFIG.H);
      grad.addColorStop(0, blobLight);
      grad.addColorStop(0.55, blobColor);
      grad.addColorStop(1, blobDark);
      ctx.fillStyle = grad;
      ctx.fill();

      /* everything below is clipped to the mound */
      ctx.save();
      ctx.clip();

      /* broad wet-jelly sheen across the upper body */
      var sheen = ctx.createLinearGradient(0, CONFIG.BLOB_TOP - 16, 0, CONFIG.BLOB_TOP + 130);
      sheen.addColorStop(0, 'rgba(255,255,255,0.55)');
      sheen.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = sheen;
      ctx.fillRect(0, CONFIG.BLOB_TOP - 24, CONFIG.W, 170);

      /* suspended air bubbles (konjac-jelly signature) */
      for (var b = 0; b < bubbles.length; b++) {
        var bu = bubbles[b];
        var by = bu.y + Math.sin(st.time * 1.1 + bu.ph) * 2;
        ctx.fillStyle = 'rgba(255,255,255,0.30)';
        ctx.beginPath();
        ctx.arc(bu.x, by, bu.r, 0, 7);
        ctx.fill();
        ctx.fillStyle = 'rgba(255,255,255,0.7)';
        ctx.beginPath();
        ctx.arc(bu.x - bu.r * 0.3, by - bu.r * 0.3, bu.r * 0.4, 0, 7);
        ctx.fill();
      }

      /* translucent depth shadow pooling at the base */
      var deep = ctx.createLinearGradient(0, CONFIG.H - 70, 0, CONFIG.H);
      deep.addColorStop(0, 'rgba(60,90,50,0)');
      deep.addColorStop(1, 'rgba(60,90,50,0.16)');
      ctx.fillStyle = deep;
      ctx.fillRect(0, CONFIG.H - 80, CONFIG.W, 80);
      ctx.restore();

      /* bright rim light riding the top surface */
      ctx.strokeStyle = 'rgba(255,255,255,0.6)';
      ctx.lineWidth = 3;
      ctx.beginPath();
      for (var rx = 0; rx <= CONFIG.W; rx += 14) {
        var ry = blobSurfaceY(rx) + 2.5;
        if (rx === 0) ctx.moveTo(rx, ry);
        else ctx.lineTo(rx, ry);
      }
      ctx.stroke();

      /* specular glints */
      ctx.fillStyle = 'rgba(255,255,255,0.75)';
      ctx.beginPath();
      ctx.ellipse(CONFIG.W * 0.28, CONFIG.BLOB_TOP + 20, 40, 7, -0.12, 0, 7);
      ctx.fill();
      ctx.fillStyle = 'rgba(255,255,255,0.4)';
      ctx.beginPath();
      ctx.ellipse(CONFIG.W * 0.64, CONFIG.BLOB_TOP + 40, 24, 5, -0.1, 0, 7);
      ctx.fill();

      ctx.restore();
    }

    /* red screen-edge flash on a hazard hit — transient damage feedback only,
       never the platform. Reinforces "red = bad". */
    function drawHitFlash() {
      if (st.flashT >= 0.28) return;
      var a = (1 - st.flashT / 0.28) * 0.5;
      var vg = ctx.createRadialGradient(
        CONFIG.W / 2, CONFIG.H / 2, CONFIG.W * 0.32,
        CONFIG.W / 2, CONFIG.H / 2, CONFIG.W * 0.78
      );
      vg.addColorStop(0, 'rgba(229,72,77,0)');
      vg.addColorStop(1, 'rgba(229,72,77,' + a.toFixed(3) + ')');
      ctx.fillStyle = vg;
      ctx.fillRect(0, 0, CONFIG.W, CONFIG.H);
    }

    function drawShadow() {
      var surface = blobSurfaceY(st.x);
      var alt = clamp((surface - (st.y + CONFIG.CHAR_W / 2)) / 520, 0, 1);
      var k = 1 - alt * 0.75;
      ctx.fillStyle = 'rgba(90, 110, 80, ' + (0.22 * k).toFixed(3) + ')';
      ctx.beginPath();
      ctx.ellipse(st.x, surface + 6, 34 * k, 8 * k, 0, 0, 7);
      ctx.fill();
    }

    function drawCharacter() {
      var entry = charImgs[st.charKey];
      var w = CONFIG.CHAR_W;
      /* contact squash (elastic recover) + subtle velocity stretch */
      var e = easeOutElastic(Math.min(st.charSquashT * 1000 / CONFIG.CHAR_SQUASH_MS, 1));
      var sy = (0.74 + (1 - 0.74) * e) * (1 + Math.min(Math.abs(st.vy) / 3200, 0.1));
      var sx = 1 / sy;
      var blink = st.invuln > 0 && ((st.time * 12) | 0) % 2 === 0;

      /* ouch shudder — damped horizontal jitter + slight rock, ~0.45s */
      var shakeX = 0, tilt = 0;
      if (st.hitShakeT < 0.45) {
        var decay = 1 - st.hitShakeT / 0.45;
        shakeX = Math.sin(st.hitShakeT * 58) * 9 * decay;
        tilt = Math.sin(st.hitShakeT * 50) * 0.11 * decay;
      }

      ctx.save();
      ctx.translate(st.x + shakeX, st.y);
      ctx.rotate(tilt);
      ctx.scale(sx, sy);
      if (blink) ctx.globalAlpha = 0.5;
      if (entry && entry.ready) {
        var h = w * (entry.img.naturalHeight / entry.img.naturalWidth);
        ctx.drawImage(entry.img, -w / 2, -h / 2, w, h);
      } else {
        /* sprite still loading — jelly placeholder */
        ctx.fillStyle = blobColor;
        ctx.beginPath();
        ctx.arc(0, 0, w * 0.42, 0, 7);
        ctx.fill();
      }
      ctx.restore();
    }

    function drawFloats() {
      ctx.textAlign = 'center';
      for (var i = 0; i < floats.length; i++) {
        var f = floats[i];
        if (!f.active) continue;
        var t = f.age / 0.9;
        ctx.globalAlpha = 1 - t * t;
        ctx.font = '700 ' + (f.bad ? 24 : 22) + 'px ' + headingFont;
        ctx.fillStyle = f.bad ? CONFIG.HAZARD_COLOR : '#3e7a53';
        ctx.fillText(f.text, f.x, f.y - t * 44);
      }
      ctx.globalAlpha = 1;
    }

    /* --- main loop (single rAF; no timers to leak) --- */
    var last = performance.now();
    function frame(now) {
      var dt = Math.min((now - last) / 1000, CONFIG.MAX_DT);
      last = now;
      update(dt);
      render();
      requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);

    /* debug/testing handle (used by the local harness; harmless in prod).
       step() drives frames manually — rAF is suspended in hidden tabs, so
       automated playtests use this instead of waiting on real frames. */
    root.pbDebug = {
      state: st,
      config: CONFIG,
      items: items,
      sprites: { good: goodSprites, hazard: hazardSprites },
      buildStoryCard: buildStoryCard,
      step: function (dt) {
        update(dt || 1 / 60);
        render();
      }
    };
  }

  /* --- mount --- */
  var mounts = document.querySelectorAll('[data-pacpac-bounce]');
  for (var m = 0; m < mounts.length; m++) initGame(mounts[m]);
})();
